import { formatMoney } from '@esportsbudget/shared';
import { Coins, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { useBillingOverview } from '@/lib/billing';

const BILLING_ROUTE = '/settings/billing';

/**
 * Баланс платформы EsportCRM в шапке (TASK #32 PHASE 3, PB-001).
 *
 * Не «Баланс» — рядом есть деньги организации (кошельки воркспейса), и два
 * разных баланса под одинаковой надписью в одном интерфейсе — верный способ
 * однажды перепутать их местами. Здесь явно баланс платформы: сколько денег
 * лежит на счету у пользователя за сами услуги EsportCRM, а не у организации.
 *
 * Использует тот же `useBillingOverview()`, что и обязательная модалка
 * региона и вкладка настроек — один запрос, один кэш, одна правда о балансе.
 * Ничего не считает заново и не хранит сумму отдельно от ответа сервера.
 *
 * Кнопка «Пополнить» ведёт в раздел биллинга и не мутирует баланс сама —
 * настоящего пользовательского top-up ещё нет (PB-008), а прикидываться, что
 * он есть, хуже, чем честно вести в раздел, где написано, что пополнение
 * скоро появится.
 */
export function BillingHeaderWidget() {
  const { t, i18n } = useTranslation(['billing', 'common']);
  const navigate = useNavigate();
  const overview = useBillingOverview();

  if (overview.isLoading) {
    return (
      <span className="hidden items-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-[13px] text-subtle sm:flex">
        <Coins className="h-4 w-4 shrink-0" aria-hidden />
        <span className="h-3.5 w-14 animate-pulse rounded bg-surface-3" aria-hidden />
      </span>
    );
  }

  if (overview.isError || !overview.data) {
    // Ошибка запроса — не то же самое, что нулевой баланс: подменять один
    // факт другим, пусть даже визуально, нельзя.
    return (
      <span className="hidden items-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-[13px] text-subtle sm:flex">
        <Coins className="h-4 w-4 shrink-0" aria-hidden />
        {t('common:loadFailed')}
      </span>
    );
  }

  const account = overview.data;
  const balanceLabel = account.setupRequired
    ? t('billing:headerSetupRequired')
    : formatMoney(account.balanceMinor, account.currency ?? 'USD', i18n.language as 'ru' | 'en');

  return (
    <div className="flex items-center gap-1.5">
      <Link
        to={BILLING_ROUTE}
        title={t('billing:headerBalance')}
        className="flex items-center gap-2 rounded-[10px] px-2 py-1.5 text-[13px] transition-colors duration-150 hover:bg-surface-2"
      >
        <Coins className="h-4 w-4 shrink-0 text-accent" aria-hidden />

        <span className="hidden flex-col leading-tight sm:flex">
          <span className="text-[11px] text-subtle">{t('billing:headerBalance')}</span>
          <span className="font-semibold">{balanceLabel}</span>
        </span>

        {/* Узкий экран: только сумма, без подписи — иначе шапка переполняется. */}
        <span className="font-semibold sm:hidden">{balanceLabel}</span>
      </Link>

      {!account.setupRequired && (
        <Button
          variant="secondary"
          size="sm"
          className="hidden lg:inline-flex"
          icon={<Plus className="h-3.5 w-3.5" aria-hidden />}
          onClick={() => navigate(BILLING_ROUTE)}
        >
          {t('billing:headerTopUp')}
        </Button>
      )}
    </div>
  );
}
